// src/components/ItineraryBuilder.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// ITINERARY BUILDER - Trip Type 5: Multi-City Trip Planning
// Complete trip planning with flights, hotels, activities per city
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import './ItineraryBuilder.css';

// ═══════════════════════════════════════════════════════════════════════════════
// SAFE VALUE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
const safeString = (val, fallback = '') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') return fallback;
  return String(val) || fallback;
};

const safeNumber = (val, fallback = 0) => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'number' && !isNaN(val)) return val;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? fallback : parsed;
};

const safeArray = (val) => Array.isArray(val) ? val : [];

// ═══════════════════════════════════════════════════════════════════════════════
// CITY DATA - Activities, Day Trips, Excursions
// ═══════════════════════════════════════════════════════════════════════════════
const CITY_ACTIVITIES = {
  // Paris, France
  'Paris': {
    icon: '🗼',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrive in Paris', desc: 'Land at CDG, transfer to hotel, settle in' },
        { time: 'Afternoon', icon: '🗼', name: 'Eiffel Tower', desc: 'Iconic landmark, visit observation deck', price: 28, bookUrl: 'https://www.toureiffel.paris' },
        { time: 'Evening', icon: '🍷', name: 'Seine River Cruise', desc: 'Dinner cruise with city lights view', price: 75, bookUrl: 'https://www.bateaux-mouches.fr' }
      ],
      2: [
        { time: 'Morning', icon: '🎨', name: 'Louvre Museum', desc: 'World\'s largest art museum, Mona Lisa', price: 17, bookUrl: 'https://www.louvre.fr' },
        { time: 'Afternoon', icon: '🏛️', name: 'Tuileries Garden & Champs-Élysées', desc: 'Beautiful gardens, luxury shopping avenue' },
        { time: 'Evening', icon: '🎭', name: 'Moulin Rouge Show', desc: 'Famous cabaret dinner show', price: 185, bookUrl: 'https://www.moulinrouge.fr' }
      ],
      3: [
        { time: 'Morning', icon: '⛪', name: 'Sacré-Cœur & Montmartre', desc: 'Stunning basilica, artist quarter' },
        { time: 'Afternoon', icon: '🏰', name: 'Notre-Dame & Île de la Cité', desc: 'Historic cathedral, charming island' },
        { time: 'Evening', icon: '🍽️', name: 'Le Marais District', desc: 'Trendy neighborhood, Jewish quarter, great food' }
      ]
    },
    dayTrips: [
      { name: 'Versailles Palace', duration: 'Full Day', distance: '20 km', desc: 'Royal château with stunning gardens', price: 55, icon: '🏰' },
      { name: 'Giverny (Monet\'s Garden)', duration: 'Half Day', distance: '75 km', desc: 'Impressionist painter\'s home & gardens', price: 45, icon: '🌸' },
      { name: 'Champagne Region', duration: 'Full Day', distance: '150 km', desc: 'Wine tasting in famous vineyards', price: 120, icon: '🍾' }
    ],
    nearbyCity: { name: 'Versailles', reason: 'Just 30 mins away - the palace is unmissable!' }
  },

  // London, UK
  'London': {
    icon: '🇬🇧',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrive in London', desc: 'Land at LHR, Heathrow Express to city center' },
        { time: 'Afternoon', icon: '🏰', name: 'Tower of London', desc: 'Crown Jewels, historic fortress', price: 32, bookUrl: 'https://www.hrp.org.uk/tower-of-london' },
        { time: 'Evening', icon: '🌉', name: 'Tower Bridge & South Bank', desc: 'Iconic bridge, riverside walk to London Eye' }
      ],
      2: [
        { time: 'Morning', icon: '👑', name: 'Buckingham Palace', desc: 'Changing of the Guard ceremony (free)' },
        { time: 'Afternoon', icon: '🎡', name: 'London Eye & Westminster', desc: 'Big Ben, Parliament, Thames views', price: 32, bookUrl: 'https://www.londoneye.com' },
        { time: 'Evening', icon: '🎭', name: 'West End Show', desc: 'World-class theater district', price: 85, bookUrl: 'https://www.officiallondontheatre.com' }
      ],
      3: [
        { time: 'Morning', icon: '🏛️', name: 'British Museum', desc: 'World history treasures, Rosetta Stone (free)' },
        { time: 'Afternoon', icon: '🛍️', name: 'Covent Garden & Oxford Street', desc: 'Street performers, shopping' },
        { time: 'Evening', icon: '🍺', name: 'Historic Pub Tour', desc: 'Traditional pubs, fish & chips' }
      ]
    },
    dayTrips: [
      { name: 'Stonehenge & Bath', duration: 'Full Day', distance: '140 km', desc: 'Ancient monument & Roman baths', price: 89, icon: '🪨' },
      { name: 'Windsor Castle', duration: 'Half Day', distance: '40 km', desc: 'Royal residence, Queen\'s weekend home', price: 55, icon: '🏰' },
      { name: 'Cotswolds Villages', duration: 'Full Day', distance: '130 km', desc: 'Quintessential English countryside', price: 75, icon: '🏡' }
    ],
    nearbyCity: { name: 'Oxford', reason: '1 hour by train - stunning university town' }
  },

  // New York, USA
  'New York': {
    icon: '🗽',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrive in NYC', desc: 'Land at JFK, transfer to Manhattan' },
        { time: 'Afternoon', icon: '🌳', name: 'Central Park', desc: 'Iconic urban park, rent bikes or walk' },
        { time: 'Evening', icon: '🌃', name: 'Times Square', desc: 'Broadway lights, street performers' }
      ],
      2: [
        { time: 'Morning', icon: '🗽', name: 'Statue of Liberty & Ellis Island', desc: 'Iconic landmarks, immigration history', price: 24, bookUrl: 'https://www.statueofliberty.org' },
        { time: 'Afternoon', icon: '🏙️', name: '9/11 Memorial & One World', desc: 'Moving memorial, observatory views', price: 43, bookUrl: 'https://www.911memorial.org' },
        { time: 'Evening', icon: '🎭', name: 'Broadway Show', desc: 'World-famous theater', price: 150, bookUrl: 'https://www.broadway.com' }
      ],
      3: [
        { time: 'Morning', icon: '🎨', name: 'Metropolitan Museum of Art', desc: 'World-class art collection', price: 25, bookUrl: 'https://www.metmuseum.org' },
        { time: 'Afternoon', icon: '🏛️', name: 'Fifth Avenue & Rockefeller Center', desc: 'Luxury shopping, Top of the Rock' },
        { time: 'Evening', icon: '🌉', name: 'Brooklyn Bridge Sunset', desc: 'Walk across, DUMBO neighborhood' }
      ]
    },
    dayTrips: [
      { name: 'Hudson Valley', duration: 'Full Day', distance: '80 km', desc: 'Scenic drives, historic estates, wineries', price: 65, icon: '🍂' },
      { name: 'Hamptons', duration: 'Full Day', distance: '150 km', desc: 'Beach towns, celebrity spotting', price: 85, icon: '🏖️' }
    ],
    nearbyCity: { name: 'Philadelphia', reason: '90 mins by train - Liberty Bell & cheesesteaks!' }
  },

  // Tokyo, Japan
  'Tokyo': {
    icon: '🗾',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrive in Tokyo', desc: 'Land at NRT/HND, Narita Express to city' },
        { time: 'Afternoon', icon: '⛩️', name: 'Senso-ji Temple', desc: 'Ancient temple in Asakusa, Tokyo\'s oldest' },
        { time: 'Evening', icon: '🌃', name: 'Shibuya Crossing & Hachiko', desc: 'World\'s busiest intersection, nightlife' }
      ],
      2: [
        { time: 'Morning', icon: '🎌', name: 'Meiji Shrine', desc: 'Peaceful shrine in forest setting' },
        { time: 'Afternoon', icon: '🛍️', name: 'Harajuku & Takeshita Street', desc: 'Youth fashion, crepes, quirky shops' },
        { time: 'Evening', icon: '🍣', name: 'Tsukiji Outer Market', desc: 'Fresh sushi, street food paradise' }
      ],
      3: [
        { time: 'Morning', icon: '🏯', name: 'Imperial Palace East Gardens', desc: 'Beautiful gardens, historic grounds (free)' },
        { time: 'Afternoon', icon: '📺', name: 'Akihabara', desc: 'Electronics, anime, gaming paradise' },
        { time: 'Evening', icon: '🗼', name: 'Tokyo Skytree', desc: 'Tallest tower, panoramic views', price: 18, bookUrl: 'https://www.tokyo-skytree.jp' }
      ]
    },
    dayTrips: [
      { name: 'Mount Fuji & Hakone', duration: 'Full Day', distance: '100 km', desc: 'Iconic mountain, hot springs, lake cruise', price: 95, icon: '🗻' },
      { name: 'Nikko Shrines', duration: 'Full Day', distance: '140 km', desc: 'UNESCO temples in mountain setting', price: 75, icon: '⛩️' },
      { name: 'Kamakura', duration: 'Half Day', distance: '50 km', desc: 'Giant Buddha, beach town, temples', price: 45, icon: '🧘' }
    ],
    nearbyCity: { name: 'Kyoto', reason: '2 hours by Shinkansen - ancient capital is essential!' }
  },

  // Dubai, UAE
  'Dubai': {
    icon: '🌴',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrive in Dubai', desc: 'Land at DXB, hotel transfer' },
        { time: 'Afternoon', icon: '🏙️', name: 'Burj Khalifa & Dubai Mall', desc: 'World\'s tallest building, massive mall', price: 40, bookUrl: 'https://www.burjkhalifa.ae' },
        { time: 'Evening', icon: '⛲', name: 'Dubai Fountain Show', desc: 'Free show every 30 mins, dinner at Atmosphere' }
      ],
      2: [
        { time: 'Morning', icon: '🏊', name: 'Pool & Relax', desc: 'Enjoy hotel amenities, late breakfast' },
        { time: 'Afternoon', icon: '🏜️', name: 'Desert Safari', desc: 'Dune bashing, camel rides, sandboarding', price: 65, bookUrl: 'https://www.arabian-adventures.com' },
        { time: 'Evening', icon: '🎭', name: 'BBQ Dinner & Show', desc: 'Traditional BBQ, belly dancing, henna' }
      ],
      3: [
        { time: 'Morning', icon: '🛶', name: 'Abra Ride & Gold Souk', desc: 'Cross Dubai Creek, explore traditional markets' },
        { time: 'Afternoon', icon: '🏛️', name: 'Dubai Museum', desc: 'Dubai\'s history at Al Fahidi Fort' },
        { time: 'Evening', icon: '🍽️', name: 'Al Seef Dinner', desc: 'Waterfront dining with heritage vibes' }
      ],
      4: [
        { time: 'Morning', icon: '🖼️', name: 'Dubai Frame', desc: 'Iconic landmark with panoramic views', price: 15, bookUrl: 'https://www.dubaiframe.ae' },
        { time: 'Afternoon', icon: '🎢', name: 'Dubai Parks', desc: 'Theme parks for the whole family', price: 80, bookUrl: 'https://www.dubaiparksandresorts.com' },
        { time: 'Evening', icon: '🌃', name: 'JBR Beach Walk', desc: 'Beach time, street food, shopping' }
      ],
      5: [
        { time: 'Morning', icon: '🏖️', name: 'Beach Time', desc: 'Relax at JBR or Kite Beach' },
        { time: 'Afternoon', icon: '🛍️', name: 'Last-minute Shopping', desc: 'Pick up souvenirs and gifts' },
        { time: 'Evening', icon: '🚐', name: 'Transfer/Departure', desc: 'Airport transfer or next destination' }
      ]
    },
    dayTrips: [
      { name: 'Abu Dhabi', duration: 'Full Day', distance: '150 km', desc: 'Sheikh Zayed Mosque, Louvre, Corniche', price: 95, icon: '🕌' },
      { name: 'Sharjah Heritage', duration: 'Half Day', distance: '20 km', desc: 'Cultural capital, museums, souks', price: 45, icon: '🏺' }
    ],
    nearbyCity: { name: 'Abu Dhabi', reason: 'Just 1.5 hours - Grand Mosque is breathtaking!' }
  },

  // Rome, Italy
  'Rome': {
    icon: '🏛️',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrive in Rome', desc: 'Land at FCO, Leonardo Express to Termini' },
        { time: 'Afternoon', icon: '🏟️', name: 'Colosseum & Roman Forum', desc: 'Ancient amphitheater & ruins', price: 18, bookUrl: 'https://www.coopculture.it' },
        { time: 'Evening', icon: '⛲', name: 'Trevi Fountain & Spanish Steps', desc: 'Iconic landmarks, evening passeggiata' }
      ],
      2: [
        { time: 'Morning', icon: '🎨', name: 'Vatican Museums & Sistine Chapel', desc: 'Michelangelo\'s masterpiece', price: 26, bookUrl: 'https://www.museivaticani.va' },
        { time: 'Afternoon', icon: '⛪', name: 'St. Peter\'s Basilica', desc: 'World\'s largest church (free entry)' },
        { time: 'Evening', icon: '🍝', name: 'Trastevere Dinner', desc: 'Charming neighborhood, authentic Roman cuisine' }
      ],
      3: [
        { time: 'Morning', icon: '🏛️', name: 'Pantheon & Piazza Navona', desc: 'Ancient temple, beautiful square' },
        { time: 'Afternoon', icon: '🛍️', name: 'Via del Corso Shopping', desc: 'Main shopping street, gelato stops' },
        { time: 'Evening', icon: '🍷', name: 'Campo de\' Fiori', desc: 'Evening market, aperitivo culture' }
      ]
    },
    dayTrips: [
      { name: 'Pompeii & Amalfi Coast', duration: 'Full Day', distance: '250 km', desc: 'Ancient ruins & stunning coastline', price: 130, icon: '🌋' },
      { name: 'Florence', duration: 'Full Day', distance: '280 km', desc: 'Renaissance art, Duomo, Uffizi', price: 95, icon: '🎨' },
      { name: 'Tivoli Gardens', duration: 'Half Day', distance: '30 km', desc: 'Villa d\'Este fountains & gardens', price: 55, icon: '⛲' }
    ],
    nearbyCity: { name: 'Florence', reason: '1.5 hours by fast train - Renaissance masterpiece!' }
  },

  // Default for unknown cities
  'default': {
    icon: '🌍',
    activities: {
      1: [
        { time: 'Morning', icon: '🛬', name: 'Arrival Day', desc: 'Arrive and settle into accommodation' },
        { time: 'Afternoon', icon: '🚶', name: 'City Exploration', desc: 'Walk around the city center, get oriented' },
        { time: 'Evening', icon: '🍽️', name: 'Local Dinner', desc: 'Try local cuisine at a recommended restaurant' }
      ],
      2: [
        { time: 'Morning', icon: '🏛️', name: 'Main Attractions', desc: 'Visit the top sights and landmarks' },
        { time: 'Afternoon', icon: '🎨', name: 'Museum or Gallery', desc: 'Explore local culture and history' },
        { time: 'Evening', icon: '🌃', name: 'Nightlife District', desc: 'Experience the local evening scene' }
      ],
      3: [
        { time: 'Morning', icon: '🛍️', name: 'Local Markets', desc: 'Visit markets, pick up souvenirs' },
        { time: 'Afternoon', icon: '🌳', name: 'Parks & Nature', desc: 'Relax in local parks or gardens' },
        { time: 'Evening', icon: '✈️', name: 'Departure Prep', desc: 'Pack up, prepare for next destination' }
      ]
    },
    dayTrips: [],
    nearbyCity: null
  }
};

// Get city data with fallback
const getCityData = (cityName) => {
  const normalized = cityName?.trim();
  return CITY_ACTIVITIES[normalized] || CITY_ACTIVITIES['default'];
};

// Generate activities for any number of days
const generateActivities = (cityName, numDays) => {
  const cityData = getCityData(cityName);
  const activities = {};
  
  for (let day = 1; day <= numDays; day++) {
    if (cityData.activities[day]) {
      activities[day] = cityData.activities[day];
    } else {
      // Generate generic activities for extra days
      const dayMod = ((day - 1) % 3) + 1;
      const baseActivities = cityData.activities[dayMod] || cityData.activities[1];
      activities[day] = baseActivities.map(act => ({
        ...act,
        name: day > 3 ? `Day ${day}: ${act.name}` : act.name
      }));
    }
  }
  
  return activities;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const ItineraryBuilder = ({
  itinerary = {},
  flights = [],
  hotels = [],
  searchParams = {},
  onBook,
  onClose
}) => {
  // ═══════════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════════
  const [expandedSegments, setExpandedSegments] = useState({});
  const [selectedFlights, setSelectedFlights] = useState({});
  const [selectedHotels, setSelectedHotels] = useState({});
  const [selectedTransport, setSelectedTransport] = useState({});
  const [activeDays, setActiveDays] = useState({});
  const [showSidebar, setShowSidebar] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // ON-DEMAND SEARCH STATE
  // ═══════════════════════════════════════════════════════════════════════════════
  const [segmentSearchResults, setSegmentSearchResults] = useState({});
  const [searchingSegments, setSearchingSegments] = useState({});
  const [searchedSegments, setSearchedSegments] = useState({}); // Track already searched
  
  // Refs for carousels
  const carouselRefs = useRef({});

  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACT ITINERARY DATA
  // ═══════════════════════════════════════════════════════════════════════════════
  const cities = safeArray(itinerary.cities || itinerary.destinations);
  const origin = safeString(itinerary.origin || searchParams?.origin || 'Home');
  const travelers = safeNumber(itinerary.travelers || searchParams?.passengers, 1);
  const startDate = safeString(itinerary.startDate || searchParams?.departureDate);
  const endDate = safeString(itinerary.endDate || searchParams?.returnDate);
  const tripTitle = safeString(itinerary.title) || cities.map(c => safeString(c.name)).join(', ') || 'Your Trip';
  
  // Calculate total days
  const calculateDays = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : cities.reduce((sum, c) => sum + safeNumber(c.nights, 2), 0);
    }
    return cities.reduce((sum, c) => sum + safeNumber(c.nights, 2), 0);
  };
  const totalDays = calculateDays();

  // ═══════════════════════════════════════════════════════════════════════════════
  // BUILD SEGMENTS
  // ═══════════════════════════════════════════════════════════════════════════════
  const buildSegments = () => {
    const segments = [];
    let currentDate = startDate ? new Date(startDate) : new Date();
    let segmentId = 0;

    // Helper to format date for API (YYYY-MM-DD)
    const formatDateForApiInternal = (date) => {
      if (!date) return '';
      const d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };

    // Initial flight from origin to first city
    if (cities.length > 0) {
      const firstCity = cities[0];
      segments.push({
        id: `flight-${segmentId++}`,
        type: 'flight',
        from: origin,
        to: safeString(firstCity.name),
        fromCode: safeString(origin).length === 3 ? origin : safeString(origin).substring(0, 3).toUpperCase(),
        toCode: safeString(firstCity.code || firstCity.name.substring(0, 3).toUpperCase()),
        date: formatDate(currentDate),
        dateRaw: formatDateForApiInternal(currentDate),
        passengers: travelers,
        flights: safeArray(flights).filter(f => 
          !f.isReturn && 
          (safeString(f.outbound?.destination) === safeString(firstCity.code) ||
           safeString(f.destination) === safeString(firstCity.name))
        ).slice(0, 4) || generateMockFlights(origin, safeString(firstCity.name), travelers)
      });
    }

    // For each city: hotel + itinerary + transport to next
    cities.forEach((city, idx) => {
      const cityName = safeString(city.name);
      const cityCode = safeString(city.code || cityName.substring(0, 3).toUpperCase());
      const nights = safeNumber(city.nights, 2);
      const checkIn = formatDate(currentDate);
      const checkInRaw = formatDateForApiInternal(currentDate);
      const checkOutDate = new Date(currentDate);
      checkOutDate.setDate(checkOutDate.getDate() + nights);
      const checkOut = formatDate(checkOutDate);
      const checkOutRaw = formatDateForApiInternal(checkOutDate);
      
      // Is this a family stay?
      const isFamilyStay = city.stayType === 'family' || city.isFamilyStay;

      // Hotel segment
      segments.push({
        id: `hotel-${segmentId++}`,
        type: isFamilyStay ? 'family' : 'hotel',
        city: cityName,
        cityCode,
        nights,
        checkIn,
        checkOut,
        checkInRaw,
        checkOutRaw,
        rooms: Math.ceil(travelers / 2),
        hotels: isFamilyStay ? [] : (
          safeArray(hotels).filter(h => 
            safeString(h.city).toLowerCase() === cityName.toLowerCase()
          ).slice(0, 4) || generateMockHotels(cityName, nights)
        )
      });

      // Itinerary segment
      const cityData = getCityData(cityName);
      segments.push({
        id: `itinerary-${segmentId++}`,
        type: 'itinerary',
        city: cityName,
        cityCode,
        icon: cityData.icon,
        nights,
        activities: generateActivities(cityName, nights),
        dayTrips: cityData.dayTrips || [],
        nearbyCity: cityData.nearbyCity
      });

      // Update current date
      currentDate = checkOutDate;

      // Transport/Flight to next city
      const nextCity = cities[idx + 1];
      if (nextCity) {
        const nextCityName = safeString(nextCity.name);
        const distance = city.distanceToNext || 150;
        const isNearby = distance < 400;

        if (isNearby) {
          // Ground transport
          segments.push({
            id: `transport-${segmentId++}`,
            type: 'transport',
            from: cityName,
            to: nextCityName,
            date: formatDate(currentDate),
            distance: `${distance} km`,
            duration: `${Math.round(distance / 80)} hours`,
            options: [
              { type: 'private', icon: '🚐', name: 'Private Transfer', desc: 'Door-to-door, family-friendly', price: Math.round(distance * 0.8) },
              { type: 'bus', icon: '🚌', name: 'Bus/Coach', desc: 'Public transport, multiple stops', price: Math.round(distance * 0.3) },
              { type: 'taxi', icon: '🚕', name: 'Taxi/Uber', desc: 'On-demand, metered fare', price: Math.round(distance * 0.5) }
            ]
          });
        } else {
          // Flight between cities
          segments.push({
            id: `flight-${segmentId++}`,
            type: 'flight',
            from: cityName,
            to: nextCityName,
            fromCode: cityCode,
            toCode: safeString(nextCity.code || nextCityName.substring(0, 3).toUpperCase()),
            date: formatDate(currentDate),
            dateRaw: formatDateForApiInternal(currentDate),
            passengers: travelers,
            flights: generateMockFlights(cityName, nextCityName, travelers)
          });
        }
      }
    });

    // Return flight to origin
    if (cities.length > 0) {
      const lastCity = cities[cities.length - 1];
      segments.push({
        id: `flight-${segmentId++}`,
        type: 'flight',
        from: safeString(lastCity.name),
        to: origin,
        fromCode: safeString(lastCity.code || lastCity.name.substring(0, 3).toUpperCase()),
        toCode: safeString(origin).length === 3 ? origin : safeString(origin).substring(0, 3).toUpperCase(),
        date: formatDate(currentDate),
        dateRaw: formatDateForApiInternal(currentDate),
        passengers: travelers,
        isReturn: true,
        flights: safeArray(flights).filter(f => f.isReturn).slice(0, 4) || 
          generateMockFlights(safeString(lastCity.name), origin, travelers)
      });
    }

    return segments;
  };

  const segments = buildSegments();

  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════════
  function formatDate(date) {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function formatDateShort(date) {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Generate mock flights for demo
  function generateMockFlights(from, to, passengers) {
    const airlines = [
      { code: 'UA', name: 'United', color: '#0033a0' },
      { code: 'EK', name: 'Emirates', color: '#d71921' },
      { code: 'BA', name: 'British Airways', color: '#075aaa' },
      { code: 'LH', name: 'Lufthansa', color: '#05164d' }
    ];
    
    return airlines.slice(0, 3).map((airline, idx) => ({
      id: `mock-${from}-${to}-${idx}`,
      airline,
      from: from.substring(0, 3).toUpperCase(),
      to: to.substring(0, 3).toUpperCase(),
      departureTime: `${6 + idx * 4}:${idx * 15}0 ${idx < 2 ? 'AM' : 'PM'}`,
      arrivalTime: `${10 + idx * 4}:${idx * 15}0 ${idx < 1 ? 'AM' : 'PM'}`,
      duration: `${4 + idx}h ${30 - idx * 10}m`,
      stops: idx === 0 ? 0 : 1,
      price: 280 + idx * 70,
      totalPrice: (280 + idx * 70) * passengers,
      isBestPrice: idx === 0
    }));
  }

  // Generate mock hotels for demo
  function generateMockHotels(city, nights) {
    const hotelTypes = [
      { name: `Marriott ${city}`, rating: 4.5, hasKitchen: true, price: 180 },
      { name: `Hilton ${city}`, rating: 4.7, hasKitchen: false, price: 220 },
      { name: `Holiday Inn ${city}`, rating: 4.2, hasKitchen: true, price: 120 },
      { name: `Best Western ${city}`, rating: 4.0, hasKitchen: false, price: 95 }
    ];
    
    return hotelTypes.map((hotel, idx) => ({
      id: `mock-hotel-${city}-${idx}`,
      name: hotel.name,
      city,
      rating: hotel.rating,
      stars: Math.floor(hotel.rating),
      hasKitchen: hotel.hasKitchen,
      pricePerNight: hotel.price,
      totalPrice: hotel.price * nights,
      nights,
      amenities: hotel.hasKitchen ? ['Kitchen', 'WiFi', 'Pool'] : ['WiFi', 'Gym', 'Restaurant']
    }));
  }

  // Toggle segment expansion
  const toggleSegment = (segmentId) => {
    const isExpanding = !expandedSegments[segmentId];
    
    setExpandedSegments(prev => ({
      ...prev,
      [segmentId]: isExpanding
    }));
    
    // If expanding and not already searched, trigger search
    if (isExpanding && !searchedSegments[segmentId]) {
      const segment = segments.find(s => s.id === segmentId);
      if (segment && (segment.type === 'flight' || segment.type === 'hotel')) {
        searchSegment(segment);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // ON-DEMAND SEARCH FUNCTION
  // ═══════════════════════════════════════════════════════════════════════════════
  const searchSegment = async (segment) => {
    const segmentId = segment.id;
    
    // Already searching or searched
    if (searchingSegments[segmentId] || searchedSegments[segmentId]) return;
    
    // Mark as searching
    setSearchingSegments(prev => ({ ...prev, [segmentId]: true }));
    
    try {
      if (segment.type === 'flight') {
        // Search flights
        const departureDate = segment.dateRaw || formatDateForApi(segment.date);
        console.log(`✈️ Searching flights: ${segment.fromCode} → ${segment.toCode} on ${departureDate}`);
        
        const response = await fetch('/api/search-flights', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin: segment.fromCode || segment.from?.substring(0, 3).toUpperCase(),
            destination: segment.toCode || segment.to?.substring(0, 3).toUpperCase(),
            departureDate: departureDate,
            adults: segment.passengers || travelers || 1,
            travelClass: 'ECONOMY'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.flights?.length > 0) {
            // Store results
            setSegmentSearchResults(prev => ({
              ...prev,
              [segmentId]: {
                type: 'flights',
                data: data.flights.slice(0, 5).map((f, idx) => ({
                  id: f.id || `flight-${segmentId}-${idx}`,
                  airline: f.airline || { name: 'Airline', code: 'XX' },
                  from: segment.fromCode || segment.from,
                  to: segment.toCode || segment.to,
                  departureTime: f.outbound?.departureTime || f.departure?.time || f.departureTime || '9:00 AM',
                  arrivalTime: f.outbound?.arrivalTime || f.arrival?.time || f.arrivalTime || '1:00 PM',
                  duration: f.outbound?.duration || f.duration || '4h 00m',
                  stops: f.outbound?.stops ?? f.stops ?? 0,
                  price: f.price?.perPerson || f.pricePerPerson || f.price || 300,
                  totalPrice: f.price?.total || f.totalPrice || (f.price || 300) * (segment.passengers || travelers),
                  isBestPrice: idx === 0,
                  bookingUrl: f.bookingUrl || f.deepLink || null
                }))
              }
            }));
            console.log(`✈️ Found ${data.flights.length} flights for ${segment.from} → ${segment.to}`);
          } else {
            console.log(`✈️ No flights found for ${segment.from} → ${segment.to}`);
          }
        } else {
          console.error(`✈️ Flight search failed with status ${response.status}`);
        }
      } else if (segment.type === 'hotel') {
        // Search hotels
        const checkIn = segment.checkInRaw || formatDateForApi(segment.checkIn);
        const checkOut = segment.checkOutRaw || formatDateForApi(segment.checkOut);
        console.log(`🏨 Searching hotels in ${segment.city}: ${checkIn} - ${checkOut}`);
        
        const response = await fetch('/api/search-flights', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'hotels',
            city: segment.city,
            checkIn: checkIn,
            checkOut: checkOut,
            guests: segment.rooms * 2 || travelers || 2
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hotels?.length > 0) {
            // Store results
            setSegmentSearchResults(prev => ({
              ...prev,
              [segmentId]: {
                type: 'hotels',
                data: data.hotels.slice(0, 5).map((h, idx) => ({
                  id: h.id || `hotel-${segmentId}-${idx}`,
                  name: h.name || h.hotelName || `Hotel ${idx + 1}`,
                  city: segment.city,
                  rating: h.rating || 4.0,
                  stars: Math.floor(h.rating || 4),
                  hasKitchen: h.hasKitchen || h.amenities?.includes('Kitchen') || false,
                  pricePerNight: h.pricePerNight || h.price || 150,
                  totalPrice: (h.pricePerNight || h.price || 150) * segment.nights,
                  nights: segment.nights,
                  amenities: h.amenities || ['WiFi', 'Pool'],
                  location: typeof h.location === 'string' ? h.location : h.location?.address || 'City Center',
                  bookingUrl: h.bookingUrl || h.deepLink || null
                }))
              }
            }));
            console.log(`🏨 Found ${data.hotels.length} hotels in ${segment.city}`);
          } else {
            console.log(`🏨 No hotels found in ${segment.city}`);
          }
        } else {
          console.error(`🏨 Hotel search failed with status ${response.status}`);
        }
      }
    } catch (error) {
      console.error(`Search error for ${segmentId}:`, error);
    } finally {
      // Mark as searched (even if failed, to prevent repeated attempts)
      setSearchingSegments(prev => ({ ...prev, [segmentId]: false }));
      setSearchedSegments(prev => ({ ...prev, [segmentId]: true }));
    }
  };

  // Helper to format date for API (YYYY-MM-DD)
  const formatDateForApi = (dateStr) => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Try to parse the date string
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  // Get flights/hotels for a segment (from search results or mock)
  const getSegmentFlights = (segment) => {
    const results = segmentSearchResults[segment.id];
    if (results?.type === 'flights' && results.data?.length > 0) {
      return results.data;
    }
    // Return mock data if no search results yet
    return segment.flights || generateMockFlights(segment.from, segment.to, segment.passengers || travelers);
  };

  const getSegmentHotels = (segment) => {
    const results = segmentSearchResults[segment.id];
    if (results?.type === 'hotels' && results.data?.length > 0) {
      return results.data;
    }
    // Return mock data if no search results yet
    return segment.hotels || generateMockHotels(segment.city, segment.nights);
  };

  // Select flight
  const selectFlight = (segmentId, flightIdx) => {
    setSelectedFlights(prev => ({
      ...prev,
      [segmentId]: flightIdx
    }));
  };

  // Select hotel
  const selectHotel = (segmentId, hotelIdx) => {
    setSelectedHotels(prev => ({
      ...prev,
      [segmentId]: hotelIdx
    }));
  };

  // Select transport
  const selectTransport = (segmentId, optionIdx) => {
    setSelectedTransport(prev => ({
      ...prev,
      [segmentId]: optionIdx
    }));
  };

  // Select day in itinerary
  const selectDay = (segmentId, dayNum) => {
    setActiveDays(prev => ({
      ...prev,
      [segmentId]: dayNum
    }));
  };

  // Scroll carousel
  const scrollCarousel = (segmentId, direction) => {
    const ref = carouselRefs.current[segmentId];
    if (ref) {
      const scrollAmount = 240 * direction;
      ref.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let flightTotal = 0;
    let hotelTotal = 0;
    let transportTotal = 0;
    let activitiesTotal = 0;

    segments.forEach(seg => {
      if (seg.type === 'flight') {
        const selectedIdx = selectedFlights[seg.id];
        const flightOptions = getSegmentFlights(seg);
        if (selectedIdx !== undefined && flightOptions[selectedIdx]) {
          flightTotal += flightOptions[selectedIdx].totalPrice || 0;
        }
      } else if (seg.type === 'hotel') {
        const selectedIdx = selectedHotels[seg.id];
        const hotelOptions = getSegmentHotels(seg);
        if (selectedIdx !== undefined && hotelOptions[selectedIdx]) {
          hotelTotal += hotelOptions[selectedIdx].totalPrice || 0;
        }
      } else if (seg.type === 'transport') {
        const selectedIdx = selectedTransport[seg.id];
        if (selectedIdx !== undefined && seg.options[selectedIdx]) {
          transportTotal += seg.options[selectedIdx].price || 0;
        }
      }
    });

    return {
      flights: flightTotal,
      hotels: hotelTotal,
      transport: transportTotal,
      activities: activitiesTotal,
      total: flightTotal + hotelTotal + transportTotal + activitiesTotal
    };
  };

  const totals = calculateTotals();

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER FUNCTIONS (continued in next section)
  // ═══════════════════════════════════════════════════════════════════════════════

  // Render Journey Visual
  const renderJourneyVisual = () => (
    <div className="ib-journey-visual">
      <div className="ib-journey-path">
        {/* Origin */}
        <div className="ib-journey-node completed">
          <div className="ib-journey-node-icon">🏠</div>
          <div className="ib-journey-node-label">{origin}</div>
        </div>

        {cities.map((city, idx) => (
          <React.Fragment key={idx}>
            {/* Connector */}
            <div className={`ib-journey-connector ${idx === 0 ? 'completed' : ''}`}>
              <div className="ib-journey-connector-icon">
                {city.transportType === 'car' ? '🚐' : '✈️'}
              </div>
              <div className="ib-journey-connector-line"></div>
            </div>

            {/* City Node */}
            <div className={`ib-journey-node ${idx === 0 ? 'active' : ''}`}>
              <div className="ib-journey-node-icon">
                {getCityData(safeString(city.name)).icon}
              </div>
              <div className="ib-journey-node-label">{safeString(city.name)}</div>
              <div className="ib-journey-node-sub">
                {city.stayType === 'family' ? 'Family' : `${safeNumber(city.nights)} nights`}
              </div>
            </div>
          </React.Fragment>
        ))}

        {/* Return Connector & Origin */}
        <div className="ib-journey-connector">
          <div className="ib-journey-connector-icon">✈️</div>
          <div className="ib-journey-connector-line"></div>
        </div>
        <div className="ib-journey-node">
          <div className="ib-journey-node-icon">🏠</div>
          <div className="ib-journey-node-label">{origin}</div>
          <div className="ib-journey-node-sub">Home</div>
        </div>
      </div>
    </div>
  );

  // Render Flight Segment
  const renderFlightSegment = (segment) => {
    const isExpanded = expandedSegments[segment.id];
    const isSearching = searchingSegments[segment.id];
    const flightOptions = getSegmentFlights(segment);
    const selectedIdx = selectedFlights[segment.id];
    const selectedFlight = selectedIdx !== undefined ? flightOptions[selectedIdx] : null;
    const price = parseFloat(selectedFlight?.totalPrice) || parseFloat(flightOptions[0]?.totalPrice) || 0;
    const hasRealResults = segmentSearchResults[segment.id]?.type === 'flights';

    return (
      <div className={`ib-segment ${isExpanded ? 'expanded' : ''}`} key={segment.id}>
        <div className="ib-segment-header" onClick={() => toggleSegment(segment.id)}>
          <div className="ib-segment-header-left">
            <div className="ib-segment-icon flight">✈️</div>
            <div>
              <div className="ib-segment-title">{segment.from} → {segment.to}</div>
              <div className="ib-segment-subtitle">{segment.date} • {segment.passengers} passengers</div>
            </div>
          </div>
          <div className="ib-segment-header-right">
            <div className="ib-segment-price">
              {isSearching ? (
                <span className="ib-price-loading">Searching...</span>
              ) : hasRealResults ? (
                `$${price.toLocaleString()}`
              ) : (
                <span className="ib-price-estimate">~$${price.toLocaleString()}</span>
              )}
            </div>
            <div className={`ib-segment-status ${selectedFlight ? 'selected' : 'pending'}`}>
              {selectedFlight ? 'Selected' : 'Select'}
            </div>
            <div className="ib-segment-expand">▼</div>
          </div>
        </div>

        {isExpanded && (
          <div className="ib-segment-body">
            {isSearching ? (
              <div className="ib-loading">
                <div className="ib-loading-spinner">✈️</div>
                <div className="ib-loading-text">Searching for flights...</div>
              </div>
            ) : (
              <>
                <div className="ib-segment-carousel" ref={el => carouselRefs.current[segment.id] = el}>
                  {flightOptions.map((flight, idx) => {
                    const flightPrice = parseFloat(flight.price) || 0;
                    const flightTotalPrice = parseFloat(flight.totalPrice) || flightPrice * segment.passengers;
                    const stops = parseInt(flight.stops) || 0;
                    
                    return (
                    <div 
                      key={idx}
                      className={`ib-flight-card ${selectedIdx === idx ? 'selected' : ''}`}
                      onClick={() => selectFlight(segment.id, idx)}
                    >
                      {flight.isBestPrice && <div className="ib-card-badge">Best Price</div>}
                      {selectedIdx === idx && <div className="ib-card-selected-badge">✓ Selected</div>}
                      
                      <div className="ib-flight-header">
                        <div className="ib-airline">
                          <div className="ib-airline-logo">{flight.airline?.code || 'XX'}</div>
                          <div>
                            <div className="ib-airline-name">{flight.airline?.name || 'Airline'}</div>
                            <div className="ib-airline-code">{flight.flightNumber || ''}</div>
                          </div>
                        </div>
                      </div>

                      <div className="ib-flight-route">
                        <div className="ib-endpoint">
                          <div className="ib-endpoint-code">{flight.from || segment.fromCode}</div>
                          <div className="ib-endpoint-time">{flight.departureTime}</div>
                        </div>
                        <div className="ib-path">
                          <div className="ib-duration">{flight.duration}</div>
                          <div className={`ib-stops ${stops > 0 ? 'has-stops' : ''}`}>
                            {stops === 0 ? 'Nonstop' : `${stops} stop`}
                          </div>
                        </div>
                        <div className="ib-endpoint">
                          <div className="ib-endpoint-code">{flight.to || segment.toCode}</div>
                          <div className="ib-endpoint-time">{flight.arrivalTime}</div>
                        </div>
                      </div>

                      <div className="ib-flight-footer">
                        <div className="ib-price">
                          <div className="ib-price-amount">${flightPrice.toLocaleString()}</div>
                          <div className="ib-price-calc">× {segment.passengers} = ${flightTotalPrice.toLocaleString()}</div>
                        </div>
                        <button className="ib-select-btn">
                          {selectedIdx === idx ? '✓ Selected' : 'Select'}
                        </button>
                      </div>
                    </div>
                  )})}
                </div>
                
                {flightOptions.length > 2 && (
                  <div className="ib-ai-tip">
                    <span className="ib-ai-tip-icon">💡</span>
                    <span className="ib-ai-tip-text">
                      {flightOptions[1]?.stops === 0 
                        ? `Nonstop option available for just $${Math.abs((parseFloat(flightOptions[1]?.price) || 0) - (parseFloat(flightOptions[0]?.price) || 0))} more!`
                        : 'Book early for best prices - fares may increase closer to departure.'}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render Hotel Segment
  const renderHotelSegment = (segment) => {
    const isExpanded = expandedSegments[segment.id];
    const isSearching = searchingSegments[segment.id];
    const hotelOptions = getSegmentHotels(segment);
    const selectedIdx = selectedHotels[segment.id];
    const selectedHotel = selectedIdx !== undefined ? hotelOptions[selectedIdx] : null;
    const price = parseFloat(selectedHotel?.totalPrice) || parseFloat(hotelOptions[0]?.totalPrice) || 0;
    const hasRealResults = segmentSearchResults[segment.id]?.type === 'hotels';

    return (
      <div className={`ib-segment ${isExpanded ? 'expanded' : ''}`} key={segment.id}>
        <div className="ib-segment-header" onClick={() => toggleSegment(segment.id)}>
          <div className="ib-segment-header-left">
            <div className="ib-segment-icon hotel">🏨</div>
            <div>
              <div className="ib-segment-title">Stay in {segment.city}</div>
              <div className="ib-segment-subtitle">
                {segment.nights} nights • {segment.checkIn} - {segment.checkOut}
              </div>
            </div>
          </div>
          <div className="ib-segment-header-right">
            <div className="ib-segment-price">
              {isSearching ? (
                <span className="ib-price-loading">Searching...</span>
              ) : hasRealResults ? (
                `$${price.toLocaleString()}`
              ) : (
                <span className="ib-price-estimate">~$${price.toLocaleString()}</span>
              )}
            </div>
            <div className={`ib-segment-status ${selectedHotel ? 'selected' : 'pending'}`}>
              {selectedHotel ? 'Selected' : 'Select'}
            </div>
            <div className="ib-segment-expand">▼</div>
          </div>
        </div>

        {isExpanded && (
          <div className="ib-segment-body">
            {isSearching ? (
              <div className="ib-loading">
                <div className="ib-loading-spinner">🏨</div>
                <div className="ib-loading-text">Searching for hotels in {segment.city}...</div>
              </div>
            ) : (
              <>
                <div className="ib-filters-row">
                  <div className="ib-filter-chip active">🍳 Kitchen</div>
                  <div className="ib-filter-chip">👨‍👩‍👧‍👦 Family</div>
                  <div className="ib-filter-chip">🏊 Pool</div>
                </div>

                <div className="ib-segment-carousel" ref={el => carouselRefs.current[segment.id] = el}>
                  {hotelOptions.map((hotel, idx) => {
                    const rating = parseFloat(hotel.rating) || 4.0;
                    const pricePerNight = parseFloat(hotel.pricePerNight) || 0;
                    const totalPrice = parseFloat(hotel.totalPrice) || pricePerNight * segment.nights;
                    
                    return (
                    <div 
                      key={idx}
                      className={`ib-hotel-card ${selectedIdx === idx ? 'selected' : ''}`}
                      onClick={() => selectHotel(segment.id, idx)}
                    >
                      <div className="ib-hotel-image">
                        <div className="ib-hotel-rating">⭐ {rating.toFixed(1)}</div>
                        {selectedIdx === idx && <div className="ib-hotel-selected-badge">✓ Selected</div>}
                        🏨
                      </div>
                      <div className="ib-hotel-content">
                        <div className="ib-hotel-name">{hotel.name}</div>
                        <div className="ib-hotel-location">📍 {typeof hotel.location === 'string' ? hotel.location : segment.city}</div>
                        <div className="ib-hotel-tags">
                          {hotel.hasKitchen ? (
                            <span className="ib-hotel-tag success">✓ Kitchen</span>
                          ) : (
                            <span className="ib-hotel-tag warning">⚠️ No Kitchen</span>
                          )}
                        </div>
                        <div className="ib-hotel-footer">
                          <div>
                            <div className="ib-hotel-price">${pricePerNight.toLocaleString()}<span>/nt</span></div>
                            <div className="ib-hotel-total">× {segment.nights} = ${totalPrice.toLocaleString()}</div>
                          </div>
                          <button className="ib-hotel-select">
                            {selectedIdx === idx ? '✓' : 'Select'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )})}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render Family Stay Segment
  const renderFamilySegment = (segment) => {
    const isExpanded = expandedSegments[segment.id];

    return (
      <div className={`ib-segment ${isExpanded ? 'expanded' : ''}`} key={segment.id}>
        <div className="ib-segment-header" onClick={() => toggleSegment(segment.id)}>
          <div className="ib-segment-header-left">
            <div className="ib-segment-icon family">🏠</div>
            <div>
              <div className="ib-segment-title">Stay in {segment.city}</div>
              <div className="ib-segment-subtitle">{segment.nights} nights • Family</div>
            </div>
          </div>
          <div className="ib-segment-header-right">
            <div className="ib-segment-price">$0</div>
            <div className="ib-segment-status no-action">No Action Needed</div>
            <div className="ib-segment-expand">▼</div>
          </div>
        </div>

        {isExpanded && (
          <div className="ib-segment-body">
            <div className="ib-family-stay-box">
              <div className="ib-family-stay-icon">🏠</div>
              <div className="ib-family-stay-text">Staying with Family</div>
              <div className="ib-family-stay-sub">No hotel booking required</div>
              <button className="ib-add-hotel-btn">➕ Add Hotel Anyway</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Itinerary Segment
  const renderItinerarySegment = (segment) => {
    const isExpanded = expandedSegments[segment.id];
    const activeDay = activeDays[segment.id] || 1;
    const dayActivities = segment.activities[activeDay] || [];
    const cityData = getCityData(segment.city);

    const timeIcons = { 'Morning': '🌅', 'Afternoon': '☀️', 'Evening': '🌙' };

    return (
      <div className={`ib-segment ${isExpanded ? 'expanded' : ''}`} key={segment.id}>
        <div className="ib-segment-header" onClick={() => toggleSegment(segment.id)}>
          <div className="ib-segment-header-left">
            <div className="ib-segment-icon itinerary">📅</div>
            <div>
              <div className="ib-segment-title">{segment.nights}-Day {segment.city} Itinerary</div>
              <div className="ib-segment-subtitle">AI-curated daily activities</div>
            </div>
          </div>
          <div className="ib-segment-header-right">
            <div className="ib-segment-price activities">~$200 activities</div>
            <div className="ib-segment-status optional">Optional</div>
            <div className="ib-segment-expand">▼</div>
          </div>
        </div>

        {isExpanded && (
          <div className="ib-segment-body">
            {/* Day Tabs */}
            <div className="ib-day-tabs">
              {Array.from({ length: segment.nights }, (_, i) => i + 1).map(day => (
                <button
                  key={day}
                  className={`ib-day-tab ${activeDay === day ? 'active' : ''}`}
                  onClick={() => selectDay(segment.id, day)}
                >
                  Day {day}
                </button>
              ))}
            </div>

            {/* Day Content */}
            <div className="ib-day-content">
              <div className="ib-day-header">
                <div className="ib-day-number">{activeDay}</div>
                <div className="ib-day-info">
                  <div className="ib-day-title">
                    {dayActivities[0]?.name?.includes('Arrive') ? 'Arrival Day' : `Day ${activeDay} in ${segment.city}`}
                  </div>
                </div>
              </div>

              <div className="ib-activity-timeline">
                {dayActivities.map((activity, idx) => (
                  <div className="ib-activity-item" key={idx}>
                    <div className="ib-activity-time">
                      <span className="ib-time-icon">{timeIcons[activity.time]}</span>
                      <span>{activity.time}</span>
                    </div>
                    <div className="ib-activity-details">
                      <div className="ib-activity-icon">{activity.icon}</div>
                      <div className="ib-activity-info">
                        <div className="ib-activity-name">{activity.name}</div>
                        <div className="ib-activity-desc">{activity.desc}</div>
                        {activity.price && (
                          <a 
                            href={activity.bookUrl || '#'} 
                            className="ib-activity-book"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                          >
                            🎫 Book tickets (~${activity.price}/person)
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Day Trips Suggestion */}
              {segment.dayTrips?.length > 0 && activeDay === segment.nights && (
                <div className="ib-day-trips-section">
                  <h4>🚗 Worth a Day Trip</h4>
                  <div className="ib-day-trips-list">
                    {segment.dayTrips.map((trip, idx) => (
                      <div className="ib-day-trip-card" key={idx}>
                        <div className="ib-day-trip-icon">{trip.icon}</div>
                        <div className="ib-day-trip-info">
                          <div className="ib-day-trip-name">{trip.name}</div>
                          <div className="ib-day-trip-desc">{trip.desc}</div>
                          <div className="ib-day-trip-meta">
                            {trip.duration} • {trip.distance}
                          </div>
                        </div>
                        <div className="ib-day-trip-price">~${trip.price}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nearby City Suggestion */}
              {segment.nearbyCity && activeDay === 1 && (
                <div className="ib-nearby-suggestion">
                  <span className="ib-nearby-icon">💡</span>
                  <span className="ib-nearby-text">
                    <strong>Don't miss {segment.nearbyCity.name}!</strong> {segment.nearbyCity.reason}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Transport Segment
  const renderTransportSegment = (segment) => {
    const isExpanded = expandedSegments[segment.id];
    const selectedIdx = selectedTransport[segment.id];
    const selectedOption = selectedIdx !== undefined ? segment.options[selectedIdx] : null;
    const price = selectedOption?.price || segment.options[0]?.price || 0;

    return (
      <div className={`ib-segment ${isExpanded ? 'expanded' : ''}`} key={segment.id}>
        <div className="ib-segment-header" onClick={() => toggleSegment(segment.id)}>
          <div className="ib-segment-header-left">
            <div className="ib-segment-icon transport">🚐</div>
            <div>
              <div className="ib-segment-title">{segment.from} → {segment.to}</div>
              <div className="ib-segment-subtitle">{segment.date} • {segment.distance} • ~{segment.duration}</div>
            </div>
          </div>
          <div className="ib-segment-header-right">
            <div className="ib-segment-price">${price}</div>
            <div className={`ib-segment-status ${selectedOption ? 'selected' : 'pending'}`}>
              {selectedOption ? 'Selected' : 'Select'}
            </div>
            <div className="ib-segment-expand">▼</div>
          </div>
        </div>

        {isExpanded && (
          <div className="ib-segment-body">
            <div className="ib-segment-carousel" ref={el => carouselRefs.current[segment.id] = el}>
              {segment.options.map((option, idx) => (
                <div 
                  key={idx}
                  className={`ib-transport-card ${selectedIdx === idx ? 'selected' : ''}`}
                  onClick={() => selectTransport(segment.id, idx)}
                >
                  <div className="ib-transport-icon">{option.icon}</div>
                  <div className="ib-transport-name">{option.name}</div>
                  <div className="ib-transport-details">{option.desc}</div>
                  <div className="ib-transport-footer">
                    <div className="ib-transport-price">${option.price}</div>
                    <button className="ib-transport-select">
                      {selectedIdx === idx ? '✓' : 'Select'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="ib-ai-tip">
              <span className="ib-ai-tip-icon">💡</span>
              <span className="ib-ai-tip-text">
                Private transfer is recommended with kids - door-to-door service with car seats available!
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div className="ib-container">
      <div className={`ib-main-content ${showSidebar ? 'sidebar-open' : ''}`}>
        
        {/* Trip Header */}
        <div className="ib-trip-header">
          <div className="ib-trip-header-top">
            <div className="ib-trip-destination">
              <div className="ib-trip-icon">🗺️</div>
              <div className="ib-trip-title">
                <h1>{tripTitle}</h1>
                <p>{startDate && endDate ? `${formatDateShort(startDate)} - ${formatDateShort(endDate)}` : ''} • {totalDays} Days</p>
              </div>
            </div>
            <div className="ib-passenger-selector">
              <span>👨‍👩‍👧‍👦</span>
              <div className="ib-passenger-info">
                <strong>{travelers}</strong> Travelers
              </div>
              <span className="ib-edit-icon">✏️</span>
            </div>
          </div>
        </div>

        {/* Journey Visual */}
        {renderJourneyVisual()}

        {/* Segments */}
        <div className="ib-segments">
          {segments.map(segment => {
            switch (segment.type) {
              case 'flight':
                return renderFlightSegment(segment);
              case 'hotel':
                return renderHotelSegment(segment);
              case 'family':
                return renderFamilySegment(segment);
              case 'itinerary':
                return renderItinerarySegment(segment);
              case 'transport':
                return renderTransportSegment(segment);
              default:
                return null;
            }
          })}
        </div>
      </div>

      {/* Sidebar */}
      <div className={`ib-sidebar ${showSidebar ? 'open' : ''}`}>
        <div className="ib-sidebar-header">
          <div className="ib-sidebar-title">Trip Summary</div>
          <button className="ib-sidebar-close" onClick={() => setShowSidebar(false)}>×</button>
        </div>

        <div className="ib-sidebar-content">
          <div className="ib-sidebar-route-title">YOUR ROUTE</div>
          
          <div className="ib-route-steps">
            <div className="ib-route-step">
              <div className="ib-route-step-line">
                <div className="ib-route-step-dot completed"></div>
                <div className="ib-route-step-connector"></div>
              </div>
              <div className="ib-route-step-info">
                <div className="ib-route-step-city">{origin}</div>
                <div className="ib-route-step-detail">Starting Point</div>
              </div>
            </div>

            {cities.map((city, idx) => (
              <React.Fragment key={idx}>
                <div className="ib-route-step-transport">
                  ✈️ Flight
                </div>
                <div className="ib-route-step">
                  <div className="ib-route-step-line">
                    <div className={`ib-route-step-dot ${idx === 0 ? 'active' : ''}`}></div>
                    {idx < cities.length - 1 && <div className="ib-route-step-connector"></div>}
                  </div>
                  <div className="ib-route-step-info">
                    <div className="ib-route-step-city">{safeString(city.name)}</div>
                    <div className="ib-route-step-detail">{safeNumber(city.nights)} nights</div>
                  </div>
                </div>
              </React.Fragment>
            ))}

            <div className="ib-route-step-transport">
              ✈️ Return Flight
            </div>
            <div className="ib-route-step">
              <div className="ib-route-step-line">
                <div className="ib-route-step-dot"></div>
              </div>
              <div className="ib-route-step-info">
                <div className="ib-route-step-city">{origin}</div>
                <div className="ib-route-step-detail">Home</div>
              </div>
            </div>
          </div>
        </div>

        <div className="ib-sidebar-footer">
          <div className="ib-sidebar-breakdown">
            <div className="ib-breakdown-row">
              <span>✈️ Flights</span>
              <span>${totals.flights.toLocaleString()}</span>
            </div>
            <div className="ib-breakdown-row">
              <span>🏨 Hotels</span>
              <span>${totals.hotels.toLocaleString()}</span>
            </div>
            <div className="ib-breakdown-row">
              <span>🚐 Transport</span>
              <span>${totals.transport.toLocaleString()}</span>
            </div>
          </div>

          <div className="ib-sidebar-total">
            <div className="ib-sidebar-total-label">Package Total</div>
            <div className="ib-sidebar-total-amount">${totals.total.toLocaleString()}</div>
          </div>
          <div className="ib-sidebar-per-person">
            ${Math.round(totals.total / travelers).toLocaleString()} per person
          </div>

          <button 
            className="ib-review-btn"
            onClick={() => setShowBookingModal(true)}
          >
            Review & Book →
          </button>
        </div>
      </div>

      {/* Floating Sidebar Button */}
      {!showSidebar && (
        <button 
          className="ib-open-sidebar-btn"
          onClick={() => setShowSidebar(true)}
        >
          💰 ${totals.total.toLocaleString()} • View Trip
        </button>
      )}
    </div>
  );
};

export default ItineraryBuilder;
